import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import debounce from 'lodash/debounce';
import capitalize from 'lodash/capitalize';
import { Directive, ElementRef, EventEmitter, Input, Output, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { IPaginationObject } from '../reports/utils';
import { observeElement } from '../lib/utils';

const DEBOUNCE_TIME = 100;

@Directive({
  selector: '[infiniteScroll]',
  exportAs: 'infiniteScroll',
})
export class InfiniteScrollDirective implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() public infiniteScroll: string;
  @Input() public scrollElementSelector: string;
  @Input() public threshold: number;
  @Input() public pagination: IPaginationObject;
  @Input() public loadUntilOverflowEnabled = false;
  @Input() public enableCssBasedFixedHeader = false;
  @Input() public nzData: any[]; // Assuming nzData is an array of data items
  @Output() public onScrollLimit = new EventEmitter();
  private el: HTMLElement;
  private element: HTMLElement;
  private boundOnScroll: any;
  private windowResizeSubscription: Subscription;
  private filterRowResizeObserver: ResizeObserver | null = null;

  constructor(el: ElementRef) {
    this.el = el.nativeElement;
    this.element = this.el;
  }

  ngOnChanges(changes: SimpleChanges): void {
     if (changes.enableCssBasedFixedHeader && changes.enableCssBasedFixedHeader.currentValue) {
      this.initFixedHeader();
    }

    if (changes.nzData) {
      this.calculateHeightForVisibleTable();
    }
  }

  public ngOnInit() {
    if (!this.infiniteScroll) this.infiniteScroll = 'bottom';
    if (typeof this.threshold !== 'number') this.threshold = 10;
    this.boundOnScroll = debounce(this.onScroll.bind(this), DEBOUNCE_TIME).bind(this);
    if (this.scrollElementSelector) {
      const predicate: () => HTMLElement = () => this.el.querySelector(this.scrollElementSelector) as HTMLElement;
      observeElement(this.element, predicate, { childList: true, subtree: true })
        .then((element: HTMLElement) => {
          element.addEventListener('scroll', this.boundOnScroll);
          this.el = this.element = element;
        }).catch(console.error);
    } else {
      this.el.addEventListener('scroll', this.boundOnScroll);
    }

    this.windowResizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(DEBOUNCE_TIME))
      .subscribe(() => {
        if (this.enableCssBasedFixedHeader) {
          this.calculateHeightForVisibleTable();
        }
        this.loadUntilOverflow();
      });
  }

  private calculateHeightForVisibleTable() {
    const hostNzTable: HTMLElement = document.querySelector('nz-table') as HTMLElement;
    const hostNzTableBody: HTMLElement = document.querySelector('.ant-table-body') as HTMLElement;


    const filterRow = document.querySelector('phx-filter-row') as HTMLElement;
    const pageHeader = document.querySelector('page-header') as HTMLElement;
    const paginator = document.querySelector('pagination-bar') as HTMLElement;
    const dynamicElements = document.querySelector('.dynamic-elements') as HTMLElement;

    if (!this.element || 
        !this.enableCssBasedFixedHeader || 
        !hostNzTable || 
        !hostNzTableBody
      ) return;


    const filterRowHeight = filterRow ? filterRow.getBoundingClientRect().height : 0;
    const pageHeaderHeight = pageHeader ? pageHeader.getBoundingClientRect().height : 0;
    const paginatorHeight = paginator ? paginator.getBoundingClientRect().height || 36 : 0;
    const dynamicElementsHeight = dynamicElements ? dynamicElements.getBoundingClientRect().height : 0;
    const totalSubtractHeight = filterRowHeight + pageHeaderHeight + paginatorHeight + dynamicElementsHeight;

    if (this.nzData && this.nzData.length === 0) {
      hostNzTableBody.style.height = `auto`;
    } else {
      hostNzTableBody.style.height = `calc(100vh - ${totalSubtractHeight}px)`;
    }
  }

  public ngAfterViewInit() {
    // Trigger next page to load when viewport is large enough to fit all items from first page
    this.initFixedHeader();
    this.loadUntilOverflow();

    const filterRowElement = document.querySelector('phx-filter-row') as HTMLElement | null;
    if (filterRowElement) {
      let lastHeight = filterRowElement.getBoundingClientRect().height;
      // Debounce the resize callback to avoid performance issues
      const debouncedCalculateHeight = debounce(() => {
        if (this.enableCssBasedFixedHeader) {
          this.calculateHeightForVisibleTable();
        }
      }, DEBOUNCE_TIME);

      this.filterRowResizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== lastHeight) {
            lastHeight = newHeight;
            debouncedCalculateHeight();
          }
        }
      });

      this.filterRowResizeObserver.observe(filterRowElement);
    }
  }

  private initFixedHeader() {
     if (this.element && this.enableCssBasedFixedHeader) {
      const hostNzTable: HTMLElement = document.querySelector('nz-table') as HTMLElement;
      hostNzTable.classList.add('css-based-fixed-header');
      requestAnimationFrame(() => this.calculateHeightForVisibleTable());
    }
  }

  public ngOnDestroy() {
    this.el.removeEventListener('scroll', this.boundOnScroll);
    if (this.windowResizeSubscription) {
      this.windowResizeSubscription.unsubscribe();
    }

     if (this.filterRowResizeObserver) {
      this.filterRowResizeObserver.disconnect();
      this.filterRowResizeObserver = null;
    }
  }

  public scrollToTop() {
    this.element.scrollTop = 0;
  }

  public scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight;
  }

  public isAtTop() {
    return this.element.scrollTop <= this.threshold;
  }

  public isAtBottom() {
    const scrollHeight = this.element.scrollTop + this.element.offsetHeight + this.threshold;
    return scrollHeight >= this.element.scrollHeight;
  }

  public scrollUp(pixels: number) {
    this.element.scrollBy(0, -pixels);
  }

  public scrollDown(pixels: number) {
    this.element.scrollBy(0, pixels);
  }

  public isNextPageAvailable() {
    const pagination = this.pagination;
    if (!pagination) return true;
    return this.pagination.totalItems >
      ((this.pagination.pageNo - 1) * this.pagination.perPage)
      + this.pagination.totalItemsInLastResponse;
  }

  public onScroll(event) {
    if (!!event.detail) {
      this.element = event.detail.element || this.el;
    }
    this.emitScrollLimit();
  }

  private emitScrollLimit() {
    const func = `isAt${capitalize(this.infiniteScroll)}`;
    if (this[func]() && this.isNextPageAvailable()) this.onScrollLimit.emit();
  }

  private loadUntilOverflow(additionalPagesLoaded = 0): void {
    // Prevent stack overflow and infinite loop, unlikely viewport will be large enough to fit all items
    if (!this.loadUntilOverflowEnabled || additionalPagesLoaded > 5) return;

    if (this.isAtBottom() && this.isNextPageAvailable()) {
      this.emitScrollLimit();

      // Wait for next repaint and check if another page needs to be loaded
      requestAnimationFrame(() => {
        if (this.isAtBottom()) {
          setTimeout(() => this.loadUntilOverflow(additionalPagesLoaded + 1), 100);
        }
      });
    }
  }
}
